import { useTranslation } from 'react-i18next'

import { getServiceErrorKey } from '../serviceErrors'
import { useConfirmationResult } from '../useConfirmationResult'

import { holderLabel } from './roles'
import { ConfirmationResultModal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { InternalServiceRoleHolder } from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'

type RevokeServiceRoleModalProps = {
  holder: InternalServiceRoleHolder
  serviceName: string
  onClose: () => void
  onChanged: () => void
}

export function RevokeServiceRoleModal({
  holder,
  serviceName,
  onClose,
  onChanged
}: Readonly<RevokeServiceRoleModalProps>) {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const [{ isPending }, { mutateAsync: revokeRole }] = useMutation(internalServiceService.revokeServiceRole)
  const result = useConfirmationResult({ onClose, onSuccess: onChanged, isBusy: isPending })
  const name = holderLabel(holder)

  const confirm = async () => {
    const revoked = await result.run(() => revokeRole({ serviceId: holder.serviceId, holderId: holder.holderId }))

    // A refusal means the roles shown were out of date, so they are read again
    if (!revoked) onChanged()
  }

  return (
    <ConfirmationResultModal
      isOpen
      state={result.state}
      title={t('modal.revokeServiceRole.title')}
      primaryButtonText={t('button.remove')}
      loadingText={t('modal.revokeServiceRole.button.removing')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={confirm}
      onClose={result.dismiss}
      onFailureClose={result.backToConfirm}
      loading={isPending}
      primaryBtnTestId="revoke-service-role-submit-button"
      secondaryBtnTestId="revoke-service-role-cancel-button"
      buttonTestId="revoke-service-role-result-button"
      success={{
        title: t('modal.revokeServiceRole.success.title'),
        description: t('modal.revokeServiceRole.success.description', { name, service: serviceName }),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.revokeServiceRole.error.title'),
        description: t(getServiceErrorKey(result.error) ?? 'modal.revokeServiceRole.error.description'),
        button: t('button.close')
      }}
    >
      <Text size="small">{t('modal.revokeServiceRole.description', { name, service: serviceName })}</Text>
    </ConfirmationResultModal>
  )
}
